import { createSlice } from '@reduxjs/toolkit';

const initialState = {
	data: {},
	// data[orgID][siteID] = { people: [], teams: [], devices: [], groups: [], primary: [], loading: false }
	// `primary` = the IR PrimaryPermissionGroup per person (independent surface, NOT an ordinary
	// permission). Excluded from `people` server-side; see access saga + selectPrimaryGroupByPersonID.
	loadingGrant: false,
	loadingRevoke: false,
	loadingPrimary: false,
	// Scoped to the "Save changes" batch (savePermissionChanges) so it doesn't light up every surface
	// that watches loadingGrant/loadingRevoke. Only the modal that saved shows loading; the rest refresh.
	loadingSave: false,
	error: null
};

const ensureBucket = (state, orgID, siteID) => {
	const key = siteID || 'all';
	if (!state.data[orgID]) state.data[orgID] = {};
	if (!state.data[orgID][key]) {
		state.data[orgID][key] = { people: [], teams: [], devices: [], groups: [], primary: [], loading: false };
	}
	return key;
};

const accessSlice = createSlice({
	name: 'access',
	initialState,
	reducers: {
		// ── Fetch all permissions (single API call) ──
		getAccessPermissions: () => {},
		refreshAccessPermissions: () => {},
		getAccessPermissionsStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].loading = true;
			state.error = null;
		},
		getAccessPermissionsSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].people = data.people || [];
			state.data[orgID][key].teams = data.teams || [];
			state.data[orgID][key].devices = data.devices || [];
			state.data[orgID][key].groups = data.groups || [];
			state.data[orgID][key].primary = data.primary || [];
			state.data[orgID][key].loading = false;
			state.data[orgID][key].loaded = true;
		},
		getAccessPermissionsError(state, action) {
			const { orgID, siteID, error } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].loading = false;
			state.error = error;
		},

		// ── Grant ── API returns 200, socket handles credential propagation
		grantPermission: (state) => {
			state.loadingGrant = true;
			state.error = null;
		},
		grantPermissionSuccess: (state) => {
			state.loadingGrant = false;
		},
		grantPermissionError: (state, action) => {
			state.loadingGrant = false;
			state.error = action.payload;
		},

		// ── Revoke ── API returns 200, socket handles credential propagation
		revokePermission: (state) => {
			state.loadingRevoke = true;
			state.error = null;
		},
		revokePermissionSuccess: (state) => {
			state.loadingRevoke = false;
		},
		revokePermissionError: (state, action) => {
			state.loadingRevoke = false;
			state.error = action.payload;
		},
		revokePermissionByIDs: (state) => {
			state.loadingRevoke = true;
			state.error = null;
		},
		revokePermissionByIDsSuccess: (state) => {
			state.loadingRevoke = false;
		},
		revokePermissionByIDsError: (state, action) => {
			state.loadingRevoke = false;
			state.error = action.payload;
		},

		// ── Primary permission group (Inner Range) ──
		// PUT/DELETE /people/:personID/primary-permission-group → push to IR + mirror DB.
		setPrimaryPermissionGroup: (state) => {
			state.loadingPrimary = true;
			state.error = null;
		},
		setPrimaryPermissionGroupSuccess: (state) => {
			state.loadingPrimary = false;
		},
		setPrimaryPermissionGroupError: (state, action) => {
			state.loadingPrimary = false;
			state.error = action.payload;
		},
		clearPrimaryPermissionGroup: (state) => {
			state.loadingPrimary = true;
			state.error = null;
		},
		clearPrimaryPermissionGroupSuccess: (state) => {
			state.loadingPrimary = false;
		},
		clearPrimaryPermissionGroupError: (state, action) => {
			state.loadingPrimary = false;
			state.error = action.payload;
		},

		// ── Save Changes (sequential: member changes → revoke → grant) ──
		savePermissionChanges: (state) => {
			state.loadingSave = true;
			state.error = null;
		},
		savePermissionChangesSuccess: (state) => {
			state.loadingSave = false;
		},
		savePermissionChangesError: (state, action) => {
			state.loadingSave = false;
			state.error = action.payload;
		},

		// ── Device purchase request (emails the service team) ──
		// Fire-and-forget: the modal dispatches and closes, the saga reports the outcome in a
		// notification. No loading flag, so nothing is waiting on a *Complete to close.
		sendDeviceRequest: (state) => {
			state.error = null;
		},
		sendDeviceRequestSuccess: () => {},
		sendDeviceRequestError: (state, action) => {
			state.error = action.payload;
		}
	}
});

export const accessActions = accessSlice.actions;
export default accessSlice.reducer;
